import * as React from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  IconButton,
  AppBar,
  Toolbar,
  Divider,
  Skeleton,
  Button,
  CircularProgress,
} from "@mui/material";
import {
  ArrowBack,
  ArrowForwardIos,
  NotificationsNoneOutlined,
  CreditCardOutlined,
  FavoriteOutlined,
  ChatBubbleOutlineOutlined,
  MonetizationOnOutlined,
  EmojiPeopleOutlined,
  CheckCircleOutlineOutlined,
  CancelOutlined,
  LockOutlined,
  LoyaltyOutlined,
  AccessTimeOutlined,
  WarningAmberOutlined,
} from "@mui/icons-material";

import type { NotificationId, NotificationItem } from "./types/notifications";
import {
  getNotifications,
  markRead,
  markAllRead,
  markAllSeen,
} from "./api/notificationsApi";

// ---------------------------------------------------------------------------
// 아이콘 매핑
// ---------------------------------------------------------------------------
type IconConfig = { icon: React.ReactNode; bgColor: string; iconColor: string };

function getIconConfig(notificationId: NotificationId): IconConfig {
  switch (notificationId) {
    case 'NTF-CARD-001':
      return { icon: <CreditCardOutlined sx={{ fontSize: 20 }} />, bgColor: '#EEF2FF', iconColor: '#5C6BC0' };
    case 'NTF-CARD-002':
    case 'NTF-CHAT-002':
      return { icon: <AccessTimeOutlined sx={{ fontSize: 20 }} />, bgColor: '#FFF8E1', iconColor: '#F59E0B' };
    case 'NTF-CARD-003':
      return { icon: <AccessTimeOutlined sx={{ fontSize: 20 }} />, bgColor: '#FFF8E1', iconColor: '#F59E0B' };
    case 'NTF-CARD-004':
      return { icon: <WarningAmberOutlined sx={{ fontSize: 20 }} />, bgColor: '#FFF3E0', iconColor: '#FF6B35' };
    case 'NTF-CARD-005':
      return { icon: <CheckCircleOutlineOutlined sx={{ fontSize: 20 }} />, bgColor: '#E8F5E9', iconColor: '#43A047' };
    case 'NTF-MATCH-001':
      return { icon: <FavoriteOutlined sx={{ fontSize: 20 }} />, bgColor: '#FCE4EC', iconColor: '#FF6B6B' };
    case 'NTF-CHAT-001':
      return { icon: <ChatBubbleOutlineOutlined sx={{ fontSize: 20 }} />, bgColor: '#E3F2FD', iconColor: '#1E88E5' };
    case 'NTF-PAY-001':
    case 'NTF-PAY-002':
    case 'NTF-PAY-003':
      return { icon: <MonetizationOnOutlined sx={{ fontSize: 20 }} />, bgColor: '#FFF9E6', iconColor: '#C9A86C' };
    case 'NTF-ACC-001':
      return { icon: <EmojiPeopleOutlined sx={{ fontSize: 20 }} />, bgColor: '#E8F5E9', iconColor: '#43A047' };
    case 'NTF-ACC-002':
    case 'NTF-ACC-004':
      return { icon: <CheckCircleOutlineOutlined sx={{ fontSize: 20 }} />, bgColor: '#E8F5E9', iconColor: '#43A047' };
    case 'NTF-ACC-003':
    case 'NTF-ACC-005':
      return { icon: <CancelOutlined sx={{ fontSize: 20 }} />, bgColor: '#FEECEC', iconColor: '#E53935' };
    case 'NTF-ACC-006':
    case 'NTF-ACC-007':
      return { icon: <FavoriteOutlined sx={{ fontSize: 20 }} />, bgColor: '#FFF9E6', iconColor: '#C9A86C' };
    case 'NTF-ACC-008':
      return { icon: <LockOutlined sx={{ fontSize: 20 }} />, bgColor: '#EEF2FF', iconColor: '#5C6BC0' };
    case 'NTF-MKT-001':
    case 'NTF-MKT-002':
    case 'NTF-MKT-003':
      return { icon: <LoyaltyOutlined sx={{ fontSize: 20 }} />, bgColor: '#FCE4EC', iconColor: '#D81B60' };
    default:
      return { icon: <NotificationsNoneOutlined sx={{ fontSize: 20 }} />, bgColor: '#F5F5F5', iconColor: '#9E9E9E' };
  }
}

// ---------------------------------------------------------------------------
// 날짜 유틸
// ---------------------------------------------------------------------------
function getSectionKey(createdAt: string): string {
  const now = new Date();
  const d = new Date(createdAt);
  const todayStr = now.toDateString();
  const yesterdayDate = new Date(now); yesterdayDate.setDate(now.getDate() - 1);
  const yesterdayStr = yesterdayDate.toDateString();

  if (d.toDateString() === todayStr) return '오늘';
  if (d.toDateString() === yesterdayStr) return '어제';

  const diffDays = Math.floor((now.getTime() - d.getTime()) / 86400000);
  if (diffDays < 7) return '이번 주';

  // 7일 이상: 날짜별 고유 key (MM월 DD일)
  return `${d.getMonth() + 1}월 ${d.getDate()}일`;
}

function formatRelativeTime(createdAt: string): string {
  const now = new Date();
  const d = new Date(createdAt);
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return '방금 전';
  if (diffMins < 60) return `${diffMins}분 전`;
  if (diffHours < 24) return `${diffHours}시간 전`;

  const h = d.getHours();
  const m = d.getMinutes().toString().padStart(2, '0');
  const ampm = h < 12 ? '오전' : '오후';
  const displayH = h % 12 || 12;

  if (diffDays === 1) {
    return `어제 ${ampm} ${displayH}:${m}`;
  }
  return `${d.getMonth() + 1}월 ${d.getDate()}일 ${ampm} ${displayH}:${m}`;
}

function groupBySection(items: NotificationItem[]) {
  const order: string[] = [];
  const map = new Map<string, NotificationItem[]>();

  for (const item of items) {
    const key = getSectionKey(item.createdAt);
    if (!map.has(key)) { map.set(key, []); order.push(key); }
    map.get(key)!.push(item);
  }

  return order.map(label => ({ label, items: map.get(label)! }));
}

// ---------------------------------------------------------------------------
// 스켈레톤 아이템
// ---------------------------------------------------------------------------
function SkeletonItem() {
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, px: 2, py: 1.5, minHeight: 72 }}>
      <Skeleton variant="circular" width={40} height={40} sx={{ flexShrink: 0, mt: 0.25 }} />
      <Box sx={{ flex: 1 }}>
        <Skeleton variant="text" width="60%" height={20} />
        <Skeleton variant="text" width="85%" height={16} sx={{ mt: 0.5 }} />
        <Skeleton variant="text" width="30%" height={14} sx={{ mt: 0.5 }} />
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// 알림 아이템
// ---------------------------------------------------------------------------
type NotificationItemProps = {
  item: NotificationItem;
  onClick: (item: NotificationItem) => void;
};

function NotificationItemRow({ item, onClick }: NotificationItemProps) {
  const { icon, bgColor, iconColor } = getIconConfig(item.notificationId);

  return (
    <Box
      component="button"
      onClick={() => onClick(item)}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.5,
        width: '100%',
        minHeight: 72,
        px: 2,
        py: 1.75,
        bgcolor: item.isRead ? '#FFFFFF' : '#FFF5F5',
        border: 'none',
        borderBottom: '1px solid #F0F0F0',
        cursor: 'pointer',
        textAlign: 'left',
        transition: 'background-color 0.15s',
        '&:hover': { bgcolor: item.isRead ? '#F8F8F8' : '#FFE8E8' },
        '&:active': { bgcolor: '#F0F0F0' },
      }}
    >
      {/* 아이콘 원형 */}
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: '50%',
          bgcolor: bgColor,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          mt: 0.25,
          color: iconColor,
        }}
      >
        {icon}
      </Box>

      {/* 콘텐츠 */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.25 }}>
          {/* 미읽 인디케이터 */}
          {!item.isRead && (
            <Box
              sx={{
                width: 7,
                height: 7,
                borderRadius: '50%',
                bgcolor: '#FF6B6B',
                flexShrink: 0,
              }}
            />
          )}
          <Typography
            sx={{
              fontSize: '17px',
              fontWeight: item.isRead ? 400 : 700,
              color: '#1A1A2E',
              lineHeight: 1.35,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {item.title}
          </Typography>
        </Box>

        <Typography
          sx={{
            fontSize: '14px',
            color: '#4A5A72',
            lineHeight: 1.45,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            mb: 0.5,
          }}
        >
          {item.body}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography sx={{ fontSize: '12px', color: '#9AA3B0' }}>
            {formatRelativeTime(item.createdAt)}
          </Typography>
          {item.deeplink && (
            <ArrowForwardIos sx={{ fontSize: 11, color: '#C9A86C' }} />
          )}
        </Box>
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// 빈 상태
// ---------------------------------------------------------------------------
function EmptyState({ onGoHome }: { onGoHome: () => void }) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        flex: 1,
        pt: 10,
        gap: 1.5,
        px: 4,
        textAlign: 'center',
      }}
    >
      <NotificationsNoneOutlined sx={{ fontSize: 52, color: '#C9A86C', opacity: 0.6 }} />
      <Typography sx={{ fontSize: '17px', fontWeight: 600, color: '#1A1A2E', mt: 0.5 }}>
        아직 알림이 없어요
      </Typography>
      <Typography sx={{ fontSize: '14px', color: '#8896AB', lineHeight: 1.6 }}>
        새로운 소식이 오면 알려드릴게요
      </Typography>
      <Button
        variant="outlined"
        size="small"
        onClick={onGoHome}
        sx={{
          mt: 1,
          borderColor: '#C9A86C',
          color: '#C9A86C',
          fontSize: '14px',
          minHeight: 40,
          '&:hover': { borderColor: '#A08040', bgcolor: '#FFF9EC' },
        }}
      >
        홈으로 돌아가기
      </Button>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// 메인 페이지
// ---------------------------------------------------------------------------
export default function NotificationsPage() {
  const navigate = useNavigate();

  const [items, setItems] = React.useState<NotificationItem[]>([]);
  const [nextCursor, setNextCursor] = React.useState<string | null>(null);
  const [hasMore, setHasMore] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(true);
  const [isLoadingMore, setIsLoadingMore] = React.useState(false);

  // 무한스크롤 sentinel ref
  const sentinelRef = React.useRef<HTMLDivElement | null>(null);

  // ── 데이터 로드 ───────────────────────────────────────────────────────────

  const fetchPage = React.useCallback(
    async (cursor?: string) => {
      const data = await getNotifications(cursor);
      return data;
    },
    [],
  );

  // 최초 로드
  const loadFresh = React.useCallback(
    async () => {
      setIsLoading(true);
      setItems([]);
      try {
        const data = await fetchPage();
        setItems(data.items);
        setNextCursor(data.nextCursor);
        setHasMore(data.hasMore);
      } catch {
        // 오류 처리: 실제 API 연결 시 토스트 추가 예정
      } finally {
        setIsLoading(false);
      }
    },
    [fetchPage],
  );

  // 다음 페이지 로드
  const loadMore = React.useCallback(async () => {
    if (isLoadingMore || !hasMore || !nextCursor) return;
    setIsLoadingMore(true);
    try {
      const data = await fetchPage(nextCursor);
      setItems(prev => [...prev, ...data.items]);
      setNextCursor(data.nextCursor);
      setHasMore(data.hasMore);
    } catch {
      // 오류 시 다음 시도에 재시도
    } finally {
      setIsLoadingMore(false);
    }
  }, [isLoadingMore, hasMore, nextCursor, fetchPage]);

  // 최초 진입: markAllSeen + 첫 페이지 로드
  React.useEffect(() => {
    markAllSeen().catch(() => {});
    loadFresh();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // IntersectionObserver — sentinel이 화면에 들어오면 loadMore 실행
  React.useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasMore && !isLoadingMore) {
          loadMore();
        }
      },
      { threshold: 0.1 },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, isLoadingMore, loadMore]);

  // ── 인터랙션 핸들러 ───────────────────────────────────────────────────────

  const handleItemClick = React.useCallback(
    async (item: NotificationItem) => {
      if (!item.isRead) {
        // 낙관적 업데이트
        setItems(prev =>
          prev.map(n => (n.id === item.id ? { ...n, isRead: true } : n)),
        );
        markRead(item.id).catch(() => {});
      }
      if (item.deeplink) {
        navigate(item.deeplink);
      }
    },
    [navigate],
  );

  const handleMarkAllRead = React.useCallback(async () => {
    // 낙관적 업데이트
    setItems(prev => prev.map(n => ({ ...n, isRead: true })));
    try {
      await markAllRead();
    } catch {
      // 실패 시 원래 상태 복원은 생략 (재진입 시 서버 상태 반영)
    }
  }, []);

  // ── 파생 상태 ─────────────────────────────────────────────────────────────

  const hasUnread = items.some(n => !n.isRead);
  const sections = React.useMemo(() => groupBySection(items), [items]);

  // ── 렌더 ─────────────────────────────────────────────────────────────────

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#F5F7FA',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* 상단 AppBar */}
      <AppBar position="sticky" elevation={0} sx={{ bgcolor: '#0D1F3C' }}>
        <Toolbar sx={{ px: 1 }}>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => navigate(-1)}
            aria-label="뒤로 가기"
            sx={{ mr: 0.5 }}
          >
            <ArrowBack />
          </IconButton>

          <Typography
            variant="h6"
            sx={{
              flex: 1,
              fontWeight: 700,
              color: 'white',
              fontSize: '17px',
              letterSpacing: '-0.2px',
            }}
          >
            알림
          </Typography>

          {hasUnread && (
            <Button
              onClick={handleMarkAllRead}
              sx={{
                color: '#FF6B6B',
                fontSize: '13px',
                fontWeight: 600,
                minHeight: 36,
                px: 1.5,
                '&:hover': { bgcolor: 'rgba(255,107,107,0.1)' },
              }}
            >
              전체읽음
            </Button>
          )}
        </Toolbar>
      </AppBar>

      {/* 콘텐츠 */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* 로딩 스켈레톤 */}
        {isLoading && (
          <Box sx={{ bgcolor: 'white', mt: 1 }}>
            {[1, 2, 3].map(i => (
              <React.Fragment key={i}>
                <SkeletonItem />
                {i < 3 && <Divider />}
              </React.Fragment>
            ))}
          </Box>
        )}

        {/* 빈 상태 */}
        {!isLoading && items.length === 0 && (
          <EmptyState
            onGoHome={() => navigate('/dashboard')}
          />
        )}

        {/* 알림 목록 */}
        {!isLoading && items.length > 0 && (
          <Box sx={{ bgcolor: 'white', mt: 1 }}>
            {sections.map((section, sIdx) => (
              <Box key={section.label}>
                {/* 섹션 헤더 */}
                <Box
                  sx={{
                    px: 2,
                    py: 1,
                    bgcolor: '#F8F9FB',
                    borderBottom: '1px solid #F0F0F0',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '12px',
                      fontWeight: 600,
                      color: '#9AA3B0',
                      letterSpacing: '0.3px',
                    }}
                  >
                    {section.label}
                  </Typography>
                </Box>

                {/* 섹션 아이템들 */}
                {section.items.map(item => (
                  <NotificationItemRow
                    key={item.id}
                    item={item}
                    onClick={handleItemClick}
                  />
                ))}

                {/* 섹션 구분선 (마지막 섹션 제외) */}
                {sIdx < sections.length - 1 && (
                  <Box sx={{ height: 8, bgcolor: '#F5F7FA' }} />
                )}
              </Box>
            ))}

            {/* 무한 스크롤 sentinel */}
            <Box ref={sentinelRef} sx={{ height: 1 }} />

            {/* 더 보기 로딩 인디케이터 */}
            {isLoadingMore && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={24} sx={{ color: '#C9A86C' }} />
              </Box>
            )}

            {/* 더 이상 없음 */}
            {!hasMore && items.length > 0 && (
              <Box sx={{ py: 3, textAlign: 'center' }}>
                <Typography sx={{ fontSize: '13px', color: '#C0C7D0' }}>
                  모든 알림을 확인했어요
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}
